/**
 * Trusscore Estimator - Ceiling Calculations
 *
 * Calculates ceiling panel requirements and trims.
 * Supports both single-zone and multi-zone ceiling configurations.
 *
 * Ported from ceiling-related logic in estimator.js
 */

import { MaterialItem, ProductColor, CeilingConfig, CeilingZone, RoomDimensions } from '../types';
import { getWCBProduct, getHTrimProduct, getJTrimSnapInProduct } from '../data/productTable';
import { getOptimalPanels } from '../data/lengthTable';
import {
  PANEL_WIDTH_FEET,
  TRIM_STOCK_LENGTH,
} from './constants';

export interface ZoneBoundaryDetail {
  zoneName: string;
  longestEdgeFeet: number;
  hTrimQuantity: number;
}

export interface CeilingResult {
  panels: MaterialItem[];
  hTrims: MaterialItem[];
  jTrims: MaterialItem[];
  totalPanelCount: number;
  totalSqft: number;
  zoneBoundaryDetails?: ZoneBoundaryDetail[];
}

/**
 * Calculate number of panel strips needed to cover a width.
 *
 * @param widthFeet - Width to cover in feet
 * @returns Number of 16" (1.333') panel strips needed
 */
function calculatePanelStrips(widthFeet: number): number {
  if (widthFeet <= 0) return 0;
  return Math.ceil(widthFeet / PANEL_WIDTH_FEET);
}

/**
 * Calculate ceiling panels for a single rectangular zone.
 *
 * Matches legacy behavior: calculateSurfaceMaterials(roomLength, roomWidth, color)
 * - surfaceLength (first param) → used for lengthTable lookup (panel selection)
 * - surfaceWidth (second param) → used for row count (number of panel strips)
 *
 * @param length - Ceiling length in feet (used for panel length selection)
 * @param width - Ceiling width in feet (used for row count)
 * @param color - Panel color
 * @param zoneName - Optional zone name for tracking
 * @returns Ceiling panel materials
 */
export function calculateCeilingPanels(
  length: number,
  width: number,
  color: ProductColor,
  zoneName: string = 'Ceiling'
): MaterialItem[] {
  const panels: MaterialItem[] = [];

  if (length <= 0 || width <= 0) {
    return panels;
  }

  // Legacy behavior: surfaceLength → panel selection, surfaceWidth → row count
  // For ceiling: calculateSurfaceMaterials(roomLength, roomWidth, ceilingColor)
  const panelRunLength = length;  // Used for lengthTable lookup
  const coverageWidth = width;    // Used for calculating number of panel strips

  // Get optimal panel combination from length table
  const optimalPanels = getOptimalPanels(panelRunLength);

  // Number of panel strips to cover the width
  const numStrips = calculatePanelStrips(coverageWidth);

  // Calculate panels for each length
  const panelLengths = ['8', '10', '12', '14', '16', '20'] as const;

  for (const lengthStr of panelLengths) {
    const panelsOfLength = optimalPanels[lengthStr] || 0;

    if (panelsOfLength > 0) {
      // Total panels = panels per row × number of rows (strips)
      const totalPanelsNeeded = panelsOfLength * numStrips;
      const lengthFeet = parseInt(lengthStr, 10);

      const product = getWCBProduct(lengthFeet, color);

      if (product && totalPanelsNeeded > 0) {
        panels.push({
          productNumber: product.productNumber,
          productID: product.productID,
          name: product.name,
          color: product.color as ProductColor,
          quantity: totalPanelsNeeded,
          lengthFeet: product.lengthFeet,
          sqft: product.sqft,
          type: `WCB_Ceiling_${zoneName}`,
        });
      }
    }
  }

  return panels;
}

/**
 * Calculate H-Trim for ceiling panel joints.
 *
 * H-Trim is used where panels butt together along their length.
 * Matches legacy behavior: joints from lengthTable(surfaceLength),
 * H-trim runs across surfaceWidth.
 *
 * @param length - Ceiling length (used for lengthTable joint lookup)
 * @param width - Ceiling width (H-trim runs across this)
 * @param color - Trim color
 * @returns H-Trim material items
 */
export function calculateCeilingHTrims(
  length: number,
  width: number,
  color: ProductColor
): MaterialItem[] {
  const hTrims: MaterialItem[] = [];

  if (length <= 0 || width <= 0) {
    return hTrims;
  }

  // Legacy behavior: joints from lengthTable(roomLength), H-trim spans roomWidth
  const panelRunLength = length;
  const coverageWidth = width;

  // Get optimal panels to determine number of joints
  const optimalPanels = getOptimalPanels(panelRunLength);
  const numJoints = optimalPanels.joints;

  if (numJoints <= 0) {
    return hTrims;
  }

  // Each joint runs the full coverage width
  const totalHtrimLength = numJoints * coverageWidth;
  const quantity = Math.ceil(totalHtrimLength / TRIM_STOCK_LENGTH);

  const product = getHTrimProduct(color);

  if (product && quantity > 0) {
    hTrims.push({
      productNumber: product.productNumber,
      productID: product.productID,
      name: product.name,
      color: product.color as ProductColor,
      quantity,
      lengthFeet: product.lengthFeet,
      sqft: 0,
      type: 'H_Trim_Ceiling_Joint',
    });
  }

  return hTrims;
}

/**
 * Calculate J-Trim for ceiling perimeter.
 *
 * @param length - Ceiling length
 * @param width - Ceiling width
 * @param color - Trim color
 * @returns J-Trim material items
 */
export function calculateCeilingPerimeterJTrims(
  length: number,
  width: number,
  color: ProductColor
): MaterialItem[] {
  const jTrims: MaterialItem[] = [];

  if (length <= 0 || width <= 0) {
    return jTrims;
  }

  const perimeter = 2 * length + 2 * width;
  const quantity = Math.ceil(perimeter / TRIM_STOCK_LENGTH);

  const product = getJTrimSnapInProduct(color);

  if (product && quantity > 0) {
    jTrims.push({
      productNumber: product.productNumber,
      productID: product.productID,
      name: product.name,
      color: product.color as ProductColor,
      quantity,
      lengthFeet: product.lengthFeet,
      sqft: 0,
      type: 'J_Trim_SnapIn_Ceiling_Perimeter',
    });
  }

  return jTrims;
}

/**
 * Calculate all materials for a single ceiling zone.
 *
 * @param length - Zone length in feet
 * @param width - Zone width in feet
 * @param color - Panel color
 * @param zoneName - Optional zone name
 * @returns Complete ceiling result for the zone
 */
export function calculateCeilingZone(
  length: number,
  width: number,
  color: ProductColor,
  zoneName: string = 'Ceiling'
): CeilingResult {
  if (length <= 0 || width <= 0) {
    return {
      panels: [],
      hTrims: [],
      jTrims: [],
      totalPanelCount: 0,
      totalSqft: 0,
    };
  }

  const panels = calculateCeilingPanels(length, width, color, zoneName);
  const hTrims = calculateCeilingHTrims(length, width, color);
  const jTrims = calculateCeilingPerimeterJTrims(length, width, color);

  const totalPanelCount = panels.reduce((sum, item) => sum + item.quantity, 0);
  const totalSqft = length * width;

  return {
    panels,
    hTrims,
    jTrims,
    totalPanelCount,
    totalSqft,
  };
}

/**
 * Calculate materials for a multi-zone ceiling.
 *
 * Supports non-rectangular rooms by allowing multiple rectangular zones.
 * Each zone is calculated independently and results are aggregated.
 *
 * For secondary zones (zone 2+), additional H-Trim is included for the
 * longest dimension of each zone to account for potential zone boundaries.
 * This conservative approach covers all possible configurations:
 * - Interlocking panels (H-trim not needed, but included as safety margin)
 * - Butting panels (H-trim required at the zone boundary)
 * - Separate zones (H-trim not needed, but included as safety margin)
 *
 * @param zones - Array of ceiling zones
 * @returns Aggregated ceiling result for all zones
 */
export function calculateMultiZoneCeiling(zones: CeilingZone[]): CeilingResult {
  const result: CeilingResult = {
    panels: [],
    hTrims: [],
    jTrims: [],
    totalPanelCount: 0,
    totalSqft: 0,
  };

  if (!zones || zones.length === 0) {
    return result;
  }

  const zoneBoundaryDetails: ZoneBoundaryDetail[] = [];

  for (let i = 0; i < zones.length; i++) {
    const zone = zones[i];
    const zoneResult = calculateCeilingZone(
      zone.length,
      zone.width,
      zone.color,
      zone.name || zone.id
    );

    result.panels.push(...zoneResult.panels);
    result.hTrims.push(...zoneResult.hTrims);
    result.jTrims.push(...zoneResult.jTrims);
    result.totalPanelCount += zoneResult.totalPanelCount;
    result.totalSqft += zoneResult.totalSqft;

    // For secondary zones, add H-Trim for the longest dimension
    // to account for potential zone boundaries
    if (i > 0) {
      const longestEdge = Math.max(zone.length, zone.width);
      const boundaryHtrimQuantity = Math.ceil(longestEdge / TRIM_STOCK_LENGTH);

      if (boundaryHtrimQuantity > 0) {
        const product = getHTrimProduct(zone.color);

        if (product) {
          result.hTrims.push({
            productNumber: product.productNumber,
            productID: product.productID,
            name: product.name,
            color: product.color as ProductColor,
            quantity: boundaryHtrimQuantity,
            lengthFeet: product.lengthFeet,
            sqft: 0,
            type: 'H_Trim_Ceiling_Zone_Boundary',
          });

          zoneBoundaryDetails.push({
            zoneName: zone.name || zone.id,
            longestEdgeFeet: longestEdge,
            hTrimQuantity: boundaryHtrimQuantity,
          });
        }
      }
    }
  }

  if (zoneBoundaryDetails.length > 0) {
    result.zoneBoundaryDetails = zoneBoundaryDetails;
  }

  return result;
}

/**
 * Calculate ceiling from standard config.
 *
 * @param config - Ceiling configuration
 * @param dimensions - Room dimensions
 * @returns Ceiling result
 */
export function calculateCeilingFromConfig(
  config: CeilingConfig,
  dimensions: RoomDimensions
): CeilingResult {
  if (!config.enabled) {
    return {
      panels: [],
      hTrims: [],
      jTrims: [],
      totalPanelCount: 0,
      totalSqft: 0,
    };
  }

  return calculateCeilingZone(
    dimensions.length,
    dimensions.width,
    config.color,
    'Main Ceiling'
  );
}

/**
 * Calculate ceiling from zones config or fall back to room dimensions.
 *
 * @param zones - Optional ceiling zones
 * @param config - Ceiling configuration
 * @param dimensions - Room dimensions (fallback)
 * @returns Ceiling result
 */
export function calculateCeiling(
  zones: CeilingZone[] | undefined,
  config: CeilingConfig,
  dimensions: RoomDimensions
): CeilingResult {
  if (!config.enabled) {
    return {
      panels: [],
      hTrims: [],
      jTrims: [],
      totalPanelCount: 0,
      totalSqft: 0,
    };
  }

  // If zones are provided, use multi-zone calculation
  if (zones && zones.length > 0) {
    return calculateMultiZoneCeiling(zones);
  }

  // Otherwise, use room dimensions as single zone
  return calculateCeilingFromConfig(config, dimensions);
}

export default {
  calculateCeilingPanels,
  calculateCeilingHTrims,
  calculateCeilingPerimeterJTrims,
  calculateCeilingZone,
  calculateMultiZoneCeiling,
  calculateCeilingFromConfig,
  calculateCeiling,
};
